/**
 * TMC Header components.
 *
 * Defines three custom elements that together power the site header:
 *   <header-sticky>         Toggles `.sticked` on the outer .sticky-header
 *                           wrapper using an IntersectionObserver.
 *   <header-mobile-drawer>  Mobile drill-down drawer with a pre-rendered
 *                           panel stack.
 *   <header-megamenu>       Desktop megamenu flyout per nav item.
 *
 * Coordination between components is via bubbling CustomEvents on document:
 *   header:drawer-open      Mobile drawer opened
 *   header:drawer-close     Mobile drawer closed
 *   header:megamenu-open    Desktop megamenu opened (detail: panel element)
 *   header:megamenu-close   Desktop megamenu closed
 *   headerSticked           Header became sticky (legacy-compatible name)
 *   headerUnstuck           Header left sticky state (legacy-compatible name)
 */

class HeaderSticky extends HTMLElement {
  #observer = null;
  #target = null;
  #pendingState = null;
  #applyTimer = null;

  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;

    this.#target = this.closest('.sticky-header') || this.parentElement;
    const sentinel = document.querySelector('.header-sticky-sentinel');
    if (!this.#target || !sentinel) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        // Debounced rather than applied immediately: .sticky-header's own height
        // transitions when `.sticked` toggles, which can nudge scrollY by a
        // pixel or two via the browser's scroll anchoring — just enough to
        // re-cross this zero-height sentinel's boundary and toggle straight
        // back, repeatedly, faster than the transition can settle. Coalescing
        // rapid re-fires into whatever state is still current after a short
        // quiet period breaks that loop without being perceptible during
        // normal scrolling.
        this.#pendingState = !entry.isIntersecting;
        clearTimeout(this.#applyTimer);
        this.#applyTimer = setTimeout(() => this.#applyState(), 50);
      },
      { threshold: 0 },
    );

    this.#observer.observe(sentinel);
  }

  #applyState() {
    const isSticked = this.#pendingState;
    if (this.#target.classList.contains('sticked') === isSticked) return;
    this.#target.classList.toggle('sticked', isSticked);
    document.dispatchEvent(
      new CustomEvent(isSticked ? 'headerSticked' : 'headerUnstuck', {
        detail: this.#target,
      }),
    );
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    clearTimeout(this.#applyTimer);
  }
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'details > summary:first-of-type',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const MOBILE_BREAKPOINT = 1024;

class HeaderMobileDrawer extends HTMLElement {
  #toggles = [];
  #header = null;
  #section = null;
  #rootPanel = null;
  #activeSubpanel = null;
  #lastSubpanelOpener = null;
  #rafId = 0;
  #onToggleClick = null;
  #onKeydown = null;
  #onTrack = null;
  #onPanelOpen = null;
  #onPanelBack = null;
  #onAccordionToggle = null;
  #previouslyFocused = null;
  #inertedSiblings = [];

  connectedCallback() {
    this.#toggles = Array.from(
      document.querySelectorAll(`[data-mobile-drawer-toggle][aria-controls="${this.id}"]`),
    );
    this.#header = document.querySelector('.sticky-header .site-header');
    this.#section = this.closest('.shopify-section') || this.closest('.sticky-header');
    this.#rootPanel = this.querySelector('[data-mobile-panel="root"]');

    this.#onToggleClick = () => this.toggle();
    this.#toggles.forEach((btn) => btn.addEventListener('click', this.#onToggleClick));

    this.#onPanelOpen = (e) => {
      const el = e.currentTarget;
      if (window.innerWidth < MOBILE_BREAKPOINT) this.openSubpanel(el.dataset.mobilePanelOpen, el);
    };
    this.querySelectorAll('[data-mobile-panel-open]').forEach((btn) =>
      btn.addEventListener('click', this.#onPanelOpen),
    );

    this.#onPanelBack = () => this.closeSubpanel();
    this.querySelectorAll('[data-mobile-panel-back]').forEach((btn) =>
      btn.addEventListener('click', this.#onPanelBack),
    );

    this.#onAccordionToggle = (e) => {
      const btn = e.currentTarget;
      const accordion = btn.closest('[data-accordion]');
      if (!accordion) return;

      const willOpen = !accordion.classList.contains('is-open');
      const panel = accordion.closest('.site-header__mobile-panel');

      // Single-open: collapse any sibling accordion in the same panel first.
      panel?.querySelectorAll('[data-accordion].is-open').forEach((other) => {
        if (other === accordion) return;
        other.classList.remove('is-open');
        other
          .querySelector('[data-accordion-toggle]')
          ?.setAttribute('aria-expanded', 'false');
      });

      accordion.classList.toggle('is-open', willOpen);
      btn.setAttribute('aria-expanded', String(willOpen));
    };
    this.querySelectorAll('[data-accordion-toggle]').forEach((btn) =>
      btn.addEventListener('click', this.#onAccordionToggle),
    );

    this.#onKeydown = (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        // Esc closes the active subpanel first, then the drawer.
        if (this.#activeSubpanel) this.closeSubpanel();
        else this.close();
        return;
      }
      if (e.key === 'Tab') this.#trapFocus(e);
    };
    document.addEventListener('keydown', this.#onKeydown);

    this.#onTrack = () => {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = requestAnimationFrame(() => {
        this.#updateTopOffset();
        // Close the drawer if the viewport has grown past the mobile breakpoint
        if (this.isOpen && window.innerWidth >= MOBILE_BREAKPOINT) this.close();
      });
    };
  }

  disconnectedCallback() {
    this.#toggles.forEach((btn) => btn.removeEventListener('click', this.#onToggleClick));
    this.querySelectorAll('[data-mobile-panel-open]').forEach((btn) =>
      btn.removeEventListener('click', this.#onPanelOpen),
    );
    this.querySelectorAll('[data-mobile-panel-back]').forEach((btn) =>
      btn.removeEventListener('click', this.#onPanelBack),
    );
    this.querySelectorAll('[data-accordion-toggle]').forEach((btn) =>
      btn.removeEventListener('click', this.#onAccordionToggle),
    );
    document.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('scroll', this.#onTrack);
    window.removeEventListener('resize', this.#onTrack);
    cancelAnimationFrame(this.#rafId);
    this.#releaseInert();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  #updateTopOffset() {
    if (!this.#header) return;
    const bottom = this.#header.getBoundingClientRect().bottom;
    // Floor + 1px overlap to avoid a subpixel gap between header and drawer.
    this.style.setProperty('--drawer-top', `${Math.max(0, Math.floor(bottom) - 1)}px`);
  }

  #getFocusable() {
    // Scope focus to the currently active panel so users can't tab into
    // the inactive panel sliding underneath.
    const scope = this.#activeSubpanel || this.#rootPanel || this;
    return Array.from(scope.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('hidden') && el.offsetParent !== null,
    );
  }

  openSubpanel(panelId, opener) {
    const panel = this.querySelector(`[data-mobile-panel="${panelId}"]`);
    if (!panel || panel === this.#rootPanel) return;

    // If a different subpanel is already open, dismiss it first.
    if (this.#activeSubpanel && this.#activeSubpanel !== panel) {
      this.#activeSubpanel.classList.remove('is-active');
      this.#activeSubpanel.setAttribute('aria-hidden', 'true');
    }

    panel.classList.add('is-active');
    panel.setAttribute('aria-hidden', 'false');
    this.#rootPanel?.setAttribute('inert', '');
    this.#rootPanel?.classList.add('is-pushed');

    this.#activeSubpanel = panel;
    this.#lastSubpanelOpener = opener;
    opener?.setAttribute('aria-expanded', 'true');

    const focusable = this.#getFocusable();
    (focusable[0] || panel).focus({ preventScroll: true });
  }

  closeSubpanel() {
    if (!this.#activeSubpanel) return;
    this.#activeSubpanel.classList.remove('is-active');
    this.#activeSubpanel.setAttribute('aria-hidden', 'true');
    this.#rootPanel?.removeAttribute('inert');
    this.#rootPanel?.classList.remove('is-pushed');

    const opener = this.#lastSubpanelOpener;
    this.#activeSubpanel = null;
    this.#lastSubpanelOpener = null;

    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
      opener.focus({ preventScroll: true });
    }
  }

  #trapFocus(event) {
    const focusable = this.#getFocusable();
    if (focusable.length === 0) {
      event.preventDefault();
      this.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #applyInert() {
    // Make every sibling of our section inert so assistive tech and keyboard
    // navigation can't reach background content while the drawer is open.
    if (!this.#section?.parentElement) return;
    Array.from(this.#section.parentElement.children).forEach((sibling) => {
      if (sibling === this.#section) return;
      if (sibling.hasAttribute('inert')) return; // don't touch what's already inert
      sibling.setAttribute('inert', '');
      this.#inertedSiblings.push(sibling);
    });
  }

  #releaseInert() {
    this.#inertedSiblings.forEach((el) => el.removeAttribute('inert'));
    this.#inertedSiblings = [];
  }

  open() {
    this.hidden = false;
    this.#updateTopOffset();
    // Force a reflow so initial clip-path applies before we transition.
    void this.offsetWidth;
    this.classList.add('is-open');
    this.closest('.sticky-header')?.classList.add('is-drawer-open');
    this.#toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
    window.addEventListener('scroll', this.#onTrack, { passive: true });
    window.addEventListener('resize', this.#onTrack);

    // A11y: remember focus, inert the background, move focus into the drawer.
    this.#previouslyFocused = document.activeElement;
    this.#applyInert();
    const focusable = this.#getFocusable();
    (focusable[0] || this).focus({ preventScroll: true });

    document.dispatchEvent(new CustomEvent('header:drawer-open'));
  }

  close() {
    this.classList.remove('is-open');
    this.closest('.sticky-header')?.classList.remove('is-drawer-open');
    this.#toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = '';
    window.removeEventListener('scroll', this.#onTrack);
    window.removeEventListener('resize', this.#onTrack);

    // Reset any open subpanel without touching focus — we want focus to
    // restore to the hamburger toggle, not the chevron that opened the panel.
    if (this.#activeSubpanel) {
      this.#activeSubpanel.classList.remove('is-active');
      this.#activeSubpanel.setAttribute('aria-hidden', 'true');
      this.#lastSubpanelOpener?.setAttribute('aria-expanded', 'false');
      this.#rootPanel?.removeAttribute('inert');
      this.#rootPanel?.classList.remove('is-pushed');
      this.#activeSubpanel = null;
      this.#lastSubpanelOpener = null;
    }

    this.#releaseInert();
    // Restore focus to whatever opened us (usually the hamburger toggle).
    if (this.#previouslyFocused && typeof this.#previouslyFocused.focus === 'function') {
      this.#previouslyFocused.focus({ preventScroll: true });
    }
    this.#previouslyFocused = null;

    document.dispatchEvent(new CustomEvent('header:drawer-close'));
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }
}

class HeaderMegamenu extends HTMLElement {
  #header = null;
  #parentLi = null;
  #rafId = 0;
  #closeTimer = 0;
  #resizeObserver = null;
  #onEnter = null;
  #onLeave = null;
  #onTrack = null;
  #onKeydown = null;
  #onOtherOpen = null;

  connectedCallback() {
    this.#header = document.querySelector('.sticky-header .site-header');
    this.#parentLi = this.closest('li');
    if (!this.#parentLi) return;

    this.#onEnter = () => {
      clearTimeout(this.#closeTimer);
      this.open();
    };
    this.#onLeave = () => {
      // Brief grace period so the cursor can travel across the gap between
      // the trigger and the panel without slamming the menu shut.
      clearTimeout(this.#closeTimer);
      this.#closeTimer = setTimeout(() => this.close(), 300);
    };

    this.#parentLi.addEventListener('mouseenter', this.#onEnter);
    this.#parentLi.addEventListener('mouseleave', this.#onLeave);
    this.#parentLi.addEventListener('focusin', this.#onEnter);
    this.#parentLi.addEventListener('focusout', (e) => {
      if (!this.#parentLi.contains(e.relatedTarget)) this.#onLeave();
    });

    // Also treat the megamenu itself as part of the hover region so the
    // cursor can travel from the trigger into the panel without closing it.
    this.addEventListener('mouseenter', this.#onEnter);
    this.addEventListener('mouseleave', this.#onLeave);

    this.#onTrack = () => {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = requestAnimationFrame(() => this.#updateTopOffset());
    };
    this.#updateTopOffset();
    window.addEventListener('scroll', this.#onTrack, { passive: true });
    window.addEventListener('resize', this.#onTrack);

    // Also track header size changes (e.g. the sticked height transition
    // continuing after the user stops scrolling) — scroll events alone
    // miss this since they only fire while actively scrolling.
    if (this.#header && 'ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#onTrack);
      this.#resizeObserver.observe(this.#header);
    }

    this.#onKeydown = (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    };
    document.addEventListener('keydown', this.#onKeydown);

    // Close ourselves whenever a different megamenu opens, so only one can be
    // visible at a time.
    this.#onOtherOpen = (e) => {
      if (e.detail !== this) this.close();
    };
    document.addEventListener('header:megamenu-open', this.#onOtherOpen);
  }

  disconnectedCallback() {
    this.#parentLi?.removeEventListener('mouseenter', this.#onEnter);
    this.#parentLi?.removeEventListener('mouseleave', this.#onLeave);
    this.#parentLi?.removeEventListener('focusin', this.#onEnter);
    this.removeEventListener('mouseenter', this.#onEnter);
    this.removeEventListener('mouseleave', this.#onLeave);
    window.removeEventListener('scroll', this.#onTrack);
    window.removeEventListener('resize', this.#onTrack);
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('header:megamenu-open', this.#onOtherOpen);
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#rafId);
    clearTimeout(this.#closeTimer);
  }

  #updateTopOffset() {
    if (!this.#header) return;
    const bottom = this.#header.getBoundingClientRect().bottom;
    this.style.setProperty('--megamenu-top', `${Math.max(0, Math.floor(bottom) - 1)}px`);
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.dispatchEvent(new CustomEvent('header:megamenu-open', { detail: this }));
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.dispatchEvent(new CustomEvent('header:megamenu-close'));
  }
}

customElements.define('header-sticky', HeaderSticky);
customElements.define('header-mobile-drawer', HeaderMobileDrawer);
customElements.define('header-megamenu', HeaderMegamenu);
